/**
 * usePermissions Hook
 * 
 * Comprehensive permission checking hook that consolidates:
 * - App roles (from user_roles table)
 * - Org roles (from organization_members table)
 * - Module-level permission checks
 * - Navigation visibility helpers
 */

import { useMemo } from "react";
import { useAccessContext, type AccessContextOptions, type OrgRole } from "./useAccessContext";
import type { AppRole } from "./useUserRoles";
import {
  MODULES,
  type Module,
  type Action,
  hasPermission,
  canAccessModule as checkModuleAccess,
  getMergedPermissions,
  getRoleCapabilitySummary,
  ROLE_INFO,
  MODULE_INFO,
} from "@/lib/permissions";

export type { OrgRole } from "./useAccessContext";

export interface PermissionContext {
  // Role information
  appRoles: AppRole[];
  orgAppRolesDbOnly: AppRole[];
  /**
   * Every user_roles row the viewer holds, whatever organization it is scoped to (no JWT-metadata
   * roles). For a check the database makes account-wide: is_readonly_app_user_for_org treats a
   * platform_readonly row in any organization as read-only everywhere.
   */
  appRolesDbOnly: AppRole[];
  /** orgAppRolesDbOnly without the global null-org grants: rows scoped to exactly the active org. */
  orgOnlyAppRolesDbOnly: AppRole[];
  orgRole: OrgRole | null;
  isLoading: boolean;
  /**
   * The viewer's roles failed to load, so every `can()` is false for want of data, not because
   * access is denied. Show an error with `retryRoles`, never a no-permission state.
   */
  rolesUnavailable?: boolean;
  retryRoles?: () => void;
  /**
   * Changes whenever what RLS decides the viewer's reads by changes (organization, roles, role
   * scopes); stable across token refreshes. Key a cached record on it so a same-user role change
   * fetches it again rather than keep showing what the old roles could read. Null until loaded.
   */
  accessRevision?: string | null;
  
  // Permission checks
  can: (module: Module, action: Action) => boolean;
  canAccessModule: (module: Module) => boolean;
  
  // Computed permissions
  mergedPermissions: ReturnType<typeof getMergedPermissions>;
  accessibleModules: Module[];
  
  // Role-based helpers
  isOrgAdmin: boolean;      // Can manage team members (owner or admin org role)
  isOrgOwner: boolean;      // Is the organization owner
  hasAnyAppRole: boolean;   // Has at least one app role
  isSuperAdmin: boolean;    // Is IEG staff
  
  // Display helpers
  getRoleLabel: (role: AppRole) => string;
  getRoleDescription: (role: AppRole) => string;
  getModuleLabel: (module: Module) => string;
  getCapabilitySummary: () => string[];
}

export function usePermissions(options: AccessContextOptions = {}): PermissionContext {
  const { data: accessContext, isLoading, rolesUnavailable, accessRevision, refetch: refetchAccessContext } = useAccessContext(options);
  // Module permissions are org-scoped UI gates. Prefer the active-org role set
  // so a grant held only in another org does not unlock actions here.
  const appRoles = accessContext?.org_app_roles ?? accessContext?.app_roles ?? [];
  const orgAppRolesDbOnly = accessContext?.org_app_roles_db_only ?? [];
  const appRolesDbOnly = accessContext?.app_roles_db_only ?? [];
  const orgOnlyAppRolesDbOnly = accessContext?.org_only_app_roles_db_only ?? [];
  const orgRole: OrgRole | null = accessContext?.org_role ?? null;
  const isSuperAdmin = accessContext?.capabilities.isSuperAdmin ?? false;
  
  // Check if user is an org admin (can manage team)
  
  // Check if user is an org admin (can manage team)
  const isOrgAdmin = useMemo(() => {
    return orgRole === 'owner' || orgRole === 'admin';
  }, [orgRole]);
  
  // Check if user is the org owner
  const isOrgOwner = useMemo(() => {
    return orgRole === 'owner';
  }, [orgRole]);
  
  // Check if user has any app role
  const hasAnyAppRole = useMemo(() => {
    return appRoles.length > 0;
  }, [appRoles]);
  
  // Get merged permissions from all roles
  const mergedPermissions = useMemo(() => {
    return getMergedPermissions(appRoles);
  }, [appRoles]);
  
  // Get list of accessible modules
  const accessibleModules = useMemo((): Module[] => {
    return MODULES.filter((module) => checkModuleAccess(appRoles, module));
  }, [appRoles]);
  
  // Permission check function
  const can = useMemo(() => {
    return (module: Module, action: Action): boolean => {
      // Super admins have full access
      if (isSuperAdmin) return true;
      
      // Check against role permissions
      return hasPermission(appRoles, module, action);
    };
  }, [appRoles, isSuperAdmin]);
  
  // Module access check function
  const canAccessModule = useMemo(() => {
    return (module: Module): boolean => {
      // Super admins have full access
      if (isSuperAdmin) return true;
      
      // Check against role permissions
      return checkModuleAccess(appRoles, module);
    };
  }, [appRoles, isSuperAdmin]);
  
  // Display helper functions
  const getRoleLabel = (role: AppRole): string => {
    return ROLE_INFO[role]?.label ?? role;
  };
  
  const getRoleDescription = (role: AppRole): string => {
    return ROLE_INFO[role]?.description ?? '';
  };
  
  const getModuleLabel = (module: Module): string => {
    return MODULE_INFO[module]?.label ?? module;
  };
  
  const getCapabilitySummary = (): string[] => {
    // Merge capabilities from all roles
    const allCapabilities = new Set<string>();
    for (const role of appRoles) {
      const caps = getRoleCapabilitySummary(role);
      caps.forEach(c => allCapabilities.add(c));
    }
    return Array.from(allCapabilities);
  };
  
  return {
    appRoles,
    orgAppRolesDbOnly,
    appRolesDbOnly,
    orgOnlyAppRolesDbOnly,
    orgRole,
    isLoading,
    rolesUnavailable,
    accessRevision,
    retryRoles: () => { void refetchAccessContext(); },
    can,
    canAccessModule,
    mergedPermissions,
    accessibleModules,
    isOrgAdmin,
    isOrgOwner,
    hasAnyAppRole,
    isSuperAdmin,
    getRoleLabel,
    getRoleDescription,
    getModuleLabel,
    getCapabilitySummary,
  };
}

/**
 * Helper hook to check a specific permission.
 * Useful for components that only need one permission check.
 */
export function useCanAccess(module: Module, action: Action = 'view'): boolean {
  const { can, isLoading } = usePermissions();
  
  if (isLoading) return false;
  return can(module, action);
}

/**
 * Helper hook to check module visibility.
 * Returns true if the user can at least view the module.
 */
export function useModuleAccess(module: Module): { canAccess: boolean; isLoading: boolean } {
  const { canAccessModule, isLoading } = usePermissions();
  
  return {
    canAccess: isLoading ? false : canAccessModule(module),
    isLoading,
  };
}
